import React, { useRef, useLayoutEffect, useState, useEffect } from 'react'
import * as d3 from 'd3'
import { parseISO, format, startOfISOWeek } from 'date-fns'
import { Box } from '@mui/material'

const TOOLTIP_WIDTH = 200

const CraneDashboardChart = ({ weeklyData = [] }) => {
  const svgRef = useRef()
  const containerRef = useRef()
  const [width, setWidth] = useState(800)

  useEffect(() => {
    if (containerRef.current) {
      setWidth(containerRef.current.offsetWidth)
    }
  }, [containerRef, weeklyData])

  useLayoutEffect(() => {
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    if (!weeklyData.length) return

    const height = 220
    const margin = { top: 20, right: 120, bottom: 40, left: 50 }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    svg.attr('width', width).attr('height', height)

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    const weeks = weeklyData.map((d) => d.week)

    const x = d3.scalePoint()
      .domain(weeks)
      .range([0, innerWidth])

    const y = d3.scaleLinear()
      .domain([0, 100])
      .range([innerHeight, 0])

    // Grid lines
    g.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(y).ticks(5).tickSize(-innerWidth).tickFormat(''))
      .selectAll('line')
      .attr('stroke', '#e0e0e0')
      .attr('stroke-opacity', 0.3)

    g.selectAll('.grid .domain').remove()

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3.axisBottom(x)
          .tickValues(weeks.filter((_, i) => i % Math.max(1, Math.ceil(weeks.length / 10)) === 0))
      )
      .selectAll('text')
      .style('font-size', '10px')

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickFormat((d) => `${d}%`))
      .selectAll('text')
      .style('font-size', '10px')

    // Planning line
    const planLine = d3.line()
      .defined((d) => d.plan !== null && d.plan !== undefined)
      .x((d) => x(d.week))
      .y((d) => y(d.plan))

    g.append('path')
      .datum(weeklyData)
      .attr('fill', 'none')
      .attr('stroke', '#4CAF50')
      .attr('stroke-width', 2)
      .attr('d', planLine)

    // Ops line
    const opsLine = d3.line()
      .defined((d) => d.ops !== null && d.ops !== undefined)
      .x((d) => x(d.week))
      .y((d) => y(d.ops))

    g.append('path')
      .datum(weeklyData)
      .attr('fill', 'none')
      .attr('stroke', '#2196F3')
      .attr('stroke-width', 2)
      .attr('d', opsLine)

    // Hover
    const hoverLine = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', '#999')
      .attr('stroke-width', 1)
      .style('opacity', 0)

    const tooltip = g.append('g')
      .style('pointer-events', 'none')
      .style('opacity', 0)

    tooltip.append('rect')
      .attr('width', TOOLTIP_WIDTH)
      .attr('height', 56)
      .attr('rx', 6)
      .attr('fill', 'white')
      .attr('stroke', '#ccc')

    const tooltipText = tooltip.append('g').attr('transform', 'translate(10, 16)')

    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .on('mousemove', (event) => {
        const [mouseX] = d3.pointer(event)
        const nearestWeek = weeks.reduce((closest, current) => {
          if (!closest) return current
          return Math.abs(x(current) - mouseX) < Math.abs(x(closest) - mouseX) ? current : closest
        }, null)

        if (!nearestWeek) return

        const xPos = x(nearestWeek)
        const datum = weeklyData.find((d) => d.week === nearestWeek)

        hoverLine.attr('x1', xPos).attr('x2', xPos).style('opacity', 1)

        tooltipText.selectAll('*').remove()
        tooltipText.append('text').style('font-size', '11px').style('font-weight', 'bold').text(nearestWeek)
        tooltipText.append('text').attr('y', 16).style('font-size', '11px').attr('fill', '#4CAF50')
          .text(`Planning: ${datum?.plan ?? '-'}%`)
        tooltipText.append('text').attr('y', 32).style('font-size', '11px').attr('fill', '#2196F3')
          .text(`Logging: ${datum?.ops ?? '-'}%`)

        const tooltipX = xPos > innerWidth - TOOLTIP_WIDTH - 20 ? xPos - TOOLTIP_WIDTH - 12 : xPos + 12
        tooltip.attr('transform', `translate(${tooltipX}, 10)`).style('opacity', 1)
      })
      .on('mouseleave', () => {
        hoverLine.style('opacity', 0)
        tooltip.style('opacity', 0)
      })

    // Legend
    const legend = g.append('g').attr('transform', `translate(${innerWidth + 16}, 0)`)

    const items = [
      { name: 'Planning', color: '#4CAF50' },
      { name: 'Operator Logging', color: '#2196F3' },
    ]
    items.forEach((item, i) => {
      const yPos = i * 18
      legend.append('line').attr('x1', 0).attr('x2', 16).attr('y1', yPos).attr('y2', yPos)
        .attr('stroke', item.color).attr('stroke-width', 2)
      legend.append('text').attr('x', 22).attr('y', yPos + 4)
        .style('font-size', '11px').text(item.name)
    })
  }, [weeklyData, width])

  return (
    <Box ref={containerRef} sx={{ width: '100%', overflowX: 'auto' }}>
      <svg ref={svgRef} />
    </Box>
  )
}

export default CraneDashboardChart
